<script setup lang="ts">
import { ref, watch, onBeforeMount } from 'vue';
import Day from '../entities/Day';
import { DaysText } from '../enum/DaysText';
import {
  Contexts,
  NamedBindings,
  TranslationService,
  getContainer,
} from '@dandelion/core';
import _ from 'lodash';

const props = defineProps<{
  startDate: string;
  endDate: string;
  color: string;
  dayValues: { [key: string]: string };
}>();

const ts = getContainer(Contexts.DEFAULT).get(
  NamedBindings.TranslationService
) as TranslationService;

const emit = defineEmits<{
  (e: 'setDay', day: string, newValue: string): void;
}>();

onBeforeMount(() => {
  generateDays(startDate, endDate);
});

let months = ref<{ [key: string]: Day[] }>({});
let startDate = new Date(props.startDate);
let endDate = new Date(props.endDate);

watch(
  () => [props.startDate, props.endDate],
  ([start, end]) => {
    startDate = new Date(start);
    endDate = new Date(end);
    generateDays(startDate, endDate);
  }
);

const generateDays = (start: Date, end: Date) => {
  if (!start || !end || end < start) {
    return;
  }
  const dateDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  if (dateDiff > 1000) {
    return;
  }
  months = ref({});
  const current = start;
  while (current <= end) {
    const monthKey = `${current.toLocaleString('it-IT', {
      month: 'short',
      year: 'numeric',
    })}`;
    if (!months.value[monthKey]) {
      months.value[monthKey] = [];
    }
    const stringOfStartDate = current.toISOString().slice(0, 10);
    const stringTypeOfDay =
      props.dayValues[stringOfStartDate] ?? DaysText.DAY_NOT_COMPILED;

    months.value[monthKey].push({
      date: `${current.toISOString().slice(0, 10)}`,
      formattedDate: `${current.toLocaleString('it-IT', {
        weekday: 'short',
      })} ${current.getDate()} ${current.toLocaleString('it-IT', {
        month: 'short',
      })}`,
      typeOfDay: stringTypeOfDay,
    });
    current.setUTCDate(current.getUTCDate() + 1);
  }
};

const formatDateString = (dateString: string | number) => {
  if (dateString == 'Errore:') {
    return dateString;
  }
  const formattedString = String(dateString);
  const [firstPart, secondPart, thirdPart] = formattedString.split(' ');

  if (!thirdPart) {
    return `${capitalizeFirstLetter(firstPart)} ${secondPart}`;
  } else {
    const [weekday, day, month] = formattedString.split(' ');
    return `${capitalizeFirstLetter(weekday)} ${day} ${capitalizeFirstLetter(month)}`;
  }
};

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
const selectedDay = ref<Day | null>(null);

async function setDay(day: Day) {
  selectedDay.value = day;
  if (selectedDay.value) {
    switch (props.color) {
      case DaysText.GREY:
        selectedDay.value.typeOfDay = DaysText.DAY_NOT_COMPILED;
        break;
      case DaysText.YELLOW:
        selectedDay.value.typeOfDay = DaysText.DAY_WITH_ONE_VASOACTIVE;
        break;
      case DaysText.RED:
        selectedDay.value.typeOfDay = DaysText.DAY_WITH_TWO_VASOACTIVE;
        break;
      case DaysText.BLUE:
        selectedDay.value.typeOfDay = DaysText.DAY_WITHOUT_ASSISTENCE;
        break;
      case DaysText.PURPLE:
        selectedDay.value.typeOfDay = DaysText.DAY_WITH_ASSISTENCE;
        break;
    }
    emit('setDay', day.date, day.typeOfDay);
  }
}
</script>

<template>
  <div class="period-container">
    <h3
      v-html="ts.t('period', { start: props.startDate, end: props.endDate })"
    ></h3>
    <div v-for="(days, monthKey) in months" class="month-container">
      <span class="month-header" v-html="formatDateString(monthKey)"></span>
      <div class="days-container">
        <div
          v-for="day in days"
          :class="{
            day: true,
            nonCompiled: day.typeOfDay == DaysText.DAY_NOT_COMPILED,
            oneVasoactive: day.typeOfDay == DaysText.DAY_WITH_ONE_VASOACTIVE,
            twoOrMoreVasoactive:
              day.typeOfDay == DaysText.DAY_WITH_TWO_VASOACTIVE ||
              day.typeOfDay == DaysText.ERROR,
            withoutAssistence: day.typeOfDay == DaysText.DAY_WITHOUT_ASSISTENCE,
            withAssistence: day.typeOfDay == DaysText.DAY_WITH_ASSISTENCE,
          }"
          @click="setDay(day)"
        >
          <p v-html="formatDateString(day.formattedDate)" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.period-container {
  width: 100%;
}

.period-container h3,
.month-container {
  margin-top: 1rem;
}

.month-container {
  display: flex;
  align-items: center;
}

.month-header {
  font-weight: bold;
  width: 100px;
}

.days-container {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  box-shadow: none;
  border-radius: 0.25rem;
  cursor: pointer;
}

.day:hover {
  border: 1px solid rgb(10, 125, 179);
}

.nonCompiled {
  background-color: #ebf0f3;
}

.oneVasoactive {
  background-color: rgba(255, 255, 0, 0.5);
}

.twoOrMoreVasoactive {
  background-color: rgba(255, 0, 0, 0.5);
}

.withoutAssistence {
  background-color: rgba(0, 0, 255, 0.5) !important;
}

.withAssistence {
  background-color: rgba(225, 0, 255, 0.5) !important;
}

.day p {
  text-align: center;
  margin: 0;
  line-height: 1.2;
  max-width: min-content;
  font-size: 12px;
}
</style>
